import 'react-pdf/dist/Page/TextLayer.css';

import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { pdfjs } from 'react-pdf';
import { useNavigate, useParams } from 'react-router-dom';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { IconInfo, TrashIcon } from 'assets/icons';
import { config } from 'config';
import { useDocumentTitle } from 'core/application/hooks';
import { useListAttachmentsQuery } from 'core/attachments/api';
import { AttachmentSentStatusDropdown } from 'core/attachments/components/AttachmentSentStatusDropdown';
import { DownloadAttachmentFileButton } from 'core/attachments/components/DownloadAttachmentFileButton';
import { PrintAttachmentFileButton } from 'core/attachments/components/PrintAttachmentFileButton';
import { selectAuth } from 'core/auth/services/selectors';
import { User } from 'core/auth/types';
import { Language } from 'core/translation/i18next/i18next';
import { useAppSelector } from 'hooks';
import { commissionLanguages } from 'modules/commissions';
import { CustomerDetailSchema } from 'modules/customers';
import { useLazyGetOneCustomerQuery } from 'modules/customers/services';
import { InvoiceBasicForm } from 'modules/form';
import {
    useDeleteInvoiceMutation,
    useGetOneInvoiceQuery,
    useLazyGetInvoicePreviewPdfQuery,
    useMailInvoiceMutation,
    usePutUpdatedInvoiceMutation,
} from 'modules/invoicing/api';
import { InvoiceDetailSchema } from 'modules/invoicing/types';
import { transformInvoicingData } from 'modules/invoicing/utils';
import { PageContent, Subheader } from 'modules/layout';
import { BackButton, Button, NotFoundPage, Spinner } from 'modules/ui';
import { IMailModalProps, MailModal } from 'modules/ui/components/MailModal';
import { TMailModalFormSchema } from 'modules/ui/components/MailModal/MailModal.types';
import { simplifyGetInvoicePdfRequestBody } from 'utils';

import { Modal } from '../../../ui/components/Modal/Modal';
import { InvoiceDocument } from '../InvoiceDocument';
import { MailInvoiceAttachmentsButton } from '../MailInvoiceAttachmentsButton';

import styles from './InvoicingDetailPage.module.scss';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

export const InvoicingDetailPage = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    useDocumentTitle(t('nav.invoicing'));

    // fetch hooks
    const { data: response, isLoading, isFetching, isError, error } = useGetOneInvoiceQuery({ id: id ?? '' });
    const [getPdf] = useLazyGetInvoicePreviewPdfQuery();
    const [mailInvoice, { isSuccess, isLoading: isPostingEmail }] = useMailInvoiceMutation();
    const [getOneCustomer] = useLazyGetOneCustomerQuery();
    const [putUpdatedInvoice, { isLoading: isSubmittingFormLoading }] = usePutUpdatedInvoiceMutation();
    const [deleteInvoice, { isLoading: isSubmittingDeleting }] = useDeleteInvoiceMutation();
    const { data: attachments } = useListAttachmentsQuery(
        {
            invoiceId: response?.invoice_id ?? 0,
            withCommissionsDeliveryNotes: true,
        },
        { skip: !response?.invoice_id },
    );

    // state hooks
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [customer, setCustomer] = useState<CustomerDetailSchema | null>(null);
    const [isLoadingPdf, setIsLoadingPdf] = useState(false);
    const [forceSubmitForm, setForceSubmitForm] = useState(false);
    const [isSomeValueChanged, setIsSomeValueChanged] = useState<boolean>(false);
    const [showSendEmailConfirmationModal, setShowSendEmailConfirmationModal] = useState(false);
    const [showRemoveConfirmationPopup, setShowRemoveConfirmationPopup] = useState(false);

    // other hooks
    const { user } = useAppSelector(selectAuth);

    const fetchPdf = async (invoice: InvoiceDetailSchema, user: User, customer: CustomerDetailSchema) => {
        setIsLoadingPdf(true);

        if (invoice.commission.length && Number(invoice.pointDate) <= new Date().setHours(0, 0, 0, 0)) {
            const simplifiedBody = simplifyGetInvoicePdfRequestBody(invoice, customer, user);
            const { data: blob } = await getPdf({
                body: simplifiedBody,
            });

            if (!blob) return;
            // Create a Blob from the PDF Stream
            const file = new Blob([blob], { type: 'application/pdf' });
            // Build a URL from the file
            const fileURL = URL.createObjectURL(file);
            setBlobUrl(fileURL);
        }
        setIsLoadingPdf(false);
    };

    // fetch customer
    useEffect(() => {
        if (!response || !user) return;

        const fetchCustomer = async () => {
            const { data: customer, isSuccess } = await getOneCustomer({ id: String(response.customer_id) });

            if (isSuccess) setCustomer(customer);
        };
        fetchCustomer();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [response]);

    const mailAttachments = useMemo(() => {
        const attachmentsToInclude: TMailModalFormSchema['attachments'] = [];
        if (attachments?.length) {
            attachments.forEach(({ attachment_id, name }) => {
                attachmentsToInclude.push({ label: name, value: attachment_id });
            });
        }
        return attachmentsToInclude;
    }, [attachments]);

    const handleRemoveInvoice = async () => {
        try {
            await deleteInvoice({
                id: Number(id),
            }).unwrap();

            toast.success(t('invoicing.removed'));
            navigate(config.routes.invoicing.list);
        } catch (error) {
            toast.error(t('form.saveError'));
        } finally {
            setShowRemoveConfirmationPopup(false);
        }
    };

    if (isError) {
        if ('status' in (error as FetchBaseQueryError)) {
            const fetchError = error as FetchBaseQueryError;

            if (fetchError.status === 404) {
                return <NotFoundPage routeKey="invoices" />;
            }
        }
    }

    if (isLoading || isFetching || !id || !response || !user) return <Spinner fullScreen />;
    const fetchedData: InvoiceDetailSchema = response;

    const prepareData = async (
        data: InvoiceDetailSchema,
        editedBy: User,
        isAlreadyCreated: boolean,
        oldData?: InvoiceDetailSchema,
    ) => {
        if (!oldData) return;
        setForceSubmitForm(false);
        const formatted = transformInvoicingData({ currentData: data, editedBy, isAlreadyCreated });

        try {
            await putUpdatedInvoice({
                data: { ...formatted, invoiceSent: isSuccess ? true : data.invoiceSent },
                id,
            }).unwrap();
            toast.success(t('invoicing.updated'));
            navigate(config.routes.invoicing.list);
        } catch {
            toast.error(t('form.saveError'));
        }
    };

    const handleEmail: IMailModalProps['onSubmit'] = async ({ message, recipients }) => {
        if (!fetchedData.invoice_id) return;
        try {
            const data = await mailInvoice({
                emails: recipients,
                invoiceId: fetchedData.invoice_id,
                message,
                lang: (fetchedData?.language
                    ? commissionLanguages[fetchedData.language]
                    : commissionLanguages['čeština']) as unknown as Language,
            }).unwrap();

            // If there are any rejected emails, show an error message
            if (data.rejectedEmails && data.rejectedEmails.length > 0) {
                toast.error(data.message, { duration: 10000 });
            } else {
                toast.success(data.message);
                setShowSendEmailConfirmationModal(false);
            }
        } catch (error) {
            toast.error(t('table.actions.email.message.error'));
        }
    };

    return (
        <PageContent
            fullWidth
            subheader={
                <Subheader
                    startSlot={<BackButton />}
                    titleSlot={
                        <>
                            {fetchedData?.invoiceAttachmentId && (
                                <AttachmentSentStatusDropdown attachmentId={fetchedData?.invoiceAttachmentId} />
                            )}
                        </>
                    }
                    endSlot={
                        <div className={styles.actionButtons}>
                            {!fetchedData.exported && (
                                <Button
                                    onClick={() => setShowRemoveConfirmationPopup(true)}
                                    isLoading={isSubmittingDeleting}
                                    disabled={isSubmittingDeleting}
                                    className={styles.deleteBtn}
                                    type="button"
                                    variant="primary"
                                    danger
                                >
                                    <TrashIcon />
                                </Button>
                            )}
                            {fetchedData?.invoiceAttachmentId && (
                                <>
                                    <PrintAttachmentFileButton
                                        attachmentId={fetchedData.invoiceAttachmentId}
                                        disabled={isSomeValueChanged}
                                    />
                                    <DownloadAttachmentFileButton
                                        attachmentId={fetchedData.invoiceAttachmentId}
                                        disabled={isSomeValueChanged}
                                    />
                                </>
                            )}
                            {fetchedData?.invoice_id && (
                                <MailInvoiceAttachmentsButton
                                    invoiceId={fetchedData.invoice_id}
                                    disabled={isSomeValueChanged}
                                />
                            )}
                            <Button
                                isLoading={isSubmittingFormLoading}
                                disabled={isSubmittingFormLoading}
                                form="invoice-form"
                                type="submit"
                            >
                                {t('invoicing.form.save')}
                            </Button>
                        </div>
                    }
                    title={`${t('invoicing.form.title')} ${fetchedData?.invoiceNumber || ''}`}
                />
            }
        >
            {fetchedData && (
                <div className={styles.container}>
                    <div className={styles.form}>
                        <InvoiceBasicForm
                            fetchedData={fetchedData}
                            customer={customer}
                            prepareData={prepareData}
                            setIsSomeValueChanged={setIsSomeValueChanged}
                            forceSubmitForm={forceSubmitForm}
                            fetchPdf={fetchPdf}
                        />
                    </div>
                    <InvoiceDocument blobUrl={blobUrl} loading={isLoadingPdf} />
                </div>
            )}
            {showSendEmailConfirmationModal && (
                <MailModal
                    title={t('invoicing.sendEmailModal.label')}
                    isSubmitting={isPostingEmail}
                    onSubmit={handleEmail}
                    initialValues={{
                        attachments: mailAttachments,
                        message: t('invoicing.sendEmailModal.message', {
                            lng: commissionLanguages[fetchedData?.language],
                        }),
                        recipients: [...(customer?.email ? [{ value: customer?.email, label: customer?.email }] : [])],
                    }}
                    infoSlot={
                        <div className={styles.emailModalInfo}>
                            <IconInfo color="#738291" /> {t('invoicing.sendEmailModal.info')}
                        </div>
                    }
                    onOpenChange={setShowSendEmailConfirmationModal}
                />
            )}
            {showRemoveConfirmationPopup && (
                <Modal
                    onClick={() => setShowRemoveConfirmationPopup(false)}
                    label={t('invoicing.modalRemove.label')}
                    cancelComponent={
                        <Button
                            className={styles.btn}
                            type="button"
                            variant="secondary"
                            onClick={() => setShowRemoveConfirmationPopup(false)}
                        >
                            {t('invoicing.modalRemove.cancel')}
                        </Button>
                    }
                    approveComponent={
                        <Button
                            className={styles.btn}
                            type="button"
                            danger
                            variant="primary"
                            onClick={handleRemoveInvoice}
                        >
                            {t('invoicing.modalRemove.approve')}
                        </Button>
                    }
                />
            )}
        </PageContent>
    );
};
